import {
    useEffect, useCallback, useState, useRef
} from 'react';
import { useScrollDirection, SCROLL_DIRECTIONS } from '@common/Hooks/useScrollDirection';

/**
 * Set negative top value on the side panel widget on listing page
 * in combination with the position sticky which is set from the css
 * it makes the widget sticky when the bottom of the element gets in the viewport
 */
export const useDynamicStickyElement = (container, parent, dependencies = [], shouldBeDynamic) => {
    const [maxOffset, setMaxOffset] = useState(0);
    const offset = useRef(0);
    const stickyContainer = container.current;
    const prevScrollY = useRef(0);

    const scrollDirection = useScrollDirection();

    const handleSetMaxOffset = useCallback(() => {
        const windowHeight = window.innerHeight;
        const containerHeight = stickyContainer.offsetHeight;
        const { marginTop, marginBottom } = getComputedStyle(stickyContainer);
        const containerVerticalMargin = parseInt(marginTop, 10) + parseInt(marginBottom, 10);

        setMaxOffset(windowHeight - (containerHeight + containerVerticalMargin));
    }, [stickyContainer]);

    const handleDynamicOffset = useCallback(() => {
        const reduceOffset = Math.abs(prevScrollY.current - window.pageYOffset);
        prevScrollY.current = window.pageYOffset;
        const newOffset = scrollDirection === SCROLL_DIRECTIONS.up
            ? Math.min(0, offset.current + reduceOffset)
            : Math.max(maxOffset, offset.current - reduceOffset);

        if (newOffset >= maxOffset && newOffset <= 0) {
            offset.current = newOffset;
            stickyContainer.style.top = `${newOffset}px`;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxOffset, scrollDirection]);

    useEffect(() => {
        if (stickyContainer && maxOffset < 0) {
            if (shouldBeDynamic) {
                window.addEventListener('scroll', handleDynamicOffset);
            } else {
                offset.current = maxOffset;
            }
        }

        return () => {
            if (stickyContainer && shouldBeDynamic && maxOffset < 0) {
                window.removeEventListener('scroll', handleDynamicOffset);
            }
        };
    }, [stickyContainer, shouldBeDynamic, maxOffset, handleDynamicOffset]);

    useEffect(() => {
        if (stickyContainer) {
            handleSetMaxOffset();
        }
    }, [dependencies, handleSetMaxOffset, stickyContainer]);
};
